import React, { useCallback, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { createSchedule, deleteSchedule, fetchShows } from '../backend'
import { Box, Button, Card, CircularProgress, IconButton, MenuItem, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import PropTypes from 'prop-types'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'

// ── Helpers ────────────────────────────────────────────────────────────────

const COLORS = ['#4f46e5', '#7c3aed', '#db2777', '#ea580c', '#0d9488', '#0284c7', '#6366f1', '#059669']

function getShowColor(title) {
  let hash = 0
  for (let i = 0; i < title.length; i++) hash = title.charCodeAt(i) + ((hash << 5) - hash)
  return COLORS[Math.abs(hash) % COLORS.length]
}

function formatWeekday(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return (
    d.toLocaleDateString('fr-FR', { weekday: 'short' }).charAt(0).toUpperCase() +
    d.toLocaleDateString('fr-FR', { weekday: 'short' }).slice(1)
  )
}

function formatMonth(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return (
    d.toLocaleDateString('fr-FR', { month: 'short' }).charAt(0).toUpperCase() + d.toLocaleDateString('fr-FR', { month: 'short' }).slice(1)
  )
}

// ── Show Group with horizontal chip row ────────────────────────────────────

function ShowGroup({ show, onDeleteSchedule }) {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(true)
  const color = getShowColor(show.title)

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'visible',
        position: 'relative',
        backgroundColor: theme.palette.background.paper
      }}
    >
      {/* Accent bar */}
      <Box sx={{ position: 'absolute', left: 0, top: 16, bottom: 16, width: 3, borderRadius: 2, backgroundColor: color }} />

      <Box onClick={() => setExpanded(!expanded)} sx={{ cursor: 'pointer', pl: 3, pt: 2, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, color: 'text.primary', fontSize: 16 }}>
            {show.title}
          </Typography>
          {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </Box>

        <Typography variant='caption' sx={{ color: 'text.secondary', ml: 0.5 }}>
          {show.schedules?.length ?? 0} séance{show.schedules?.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {expanded && show.schedules?.length > 0 && (
        <Box sx={{ pl: 3, pb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {[...show.schedules]
            .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
            .map((sched) => (
              <Box
                key={sched.id}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 9999,
                  border: `1px solid ${color}30`,
                  backgroundColor: color + '10',
                  transition: 'all .15s ease',
                  cursor: 'default',
                  '&:hover': {
                    borderColor: color,
                    boxShadow: `0 2px 8px ${color}20`,
                    '& [data-chip-delete]': { opacity: 1 }
                  }
                }}
              >
                <Typography variant='caption' sx={{ color, fontWeight: 700 }}>
                  {formatWeekday(sched.date).replace('.', '')} {new Date(sched.date + 'T00:00:00').getDate()}{' '}
                  {formatMonth(sched.date).replace('.', '')}
                </Typography>
                <Typography variant='caption' sx={{ color, fontWeight: 500, opacity: 0.6 }}>
                  {' : '}
                </Typography>
                <Typography variant='caption' sx={{ color, fontWeight: 600 }}>
                  {sched.time}
                </Typography>

                <Tooltip title='Supprimer la séance'>
                  <IconButton
                    size='small'
                    sx={{ opacity: 0, transition: 'opacity .15s', color }}
                    data-chip-delete
                    onClick={() => onDeleteSchedule(show.id, sched.id)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
        </Box>
      )}

      {expanded && show.schedules?.length === 0 && (
        <Box sx={{ pl: 3, pb: 2 }}>
          <Typography variant='body2' sx={{ color: 'text.disabled', fontStyle: 'italic', mt: 1 }}>
            Aucune séance programmée
          </Typography>
        </Box>
      )}
    </Card>
  )
}

ShowGroup.propTypes = {
  show: PropTypes.shape({
    id: PropTypes.any.isRequired,
    title: PropTypes.string.isRequired,
    schedules: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.any.isRequired,
        date: PropTypes.string.isRequired,
        time: PropTypes.string.isRequired
      })
    )
  }).isRequired,
  onDeleteSchedule: PropTypes.func
}

// ── Create Form ────────────────────────────────────────────────────────────

const TIME_OPTIONS = (() => {
  const opts = []
  const minutes = ['00', '15', '30', '45']
  for (let i = 0; i < 24; i++) {
    const h = String(i).padStart(2, '0')
    for (const m of minutes) opts.push(`${h}:${m}`)
  }
  return opts
})()

export default function AdminScheduleManagement() {
  const theme = useTheme()
  const [selectedShowId, setSelectedShowId] = useState('')
  const [date, setDate] = useState(null)
  const [time, setTime] = useState('')
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState(null) // { type: 'success'|'error', message }
  const [shows, setShows] = useState([])
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  useEffect(() => {
    let cancelled = false
    fetchShows()
      .then((data) => {
        if (!cancelled) setShows(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [refreshTrigger])

  const refreshShows = useCallback(async () => {
    const data = await fetchShows()
    setShows(data)
    setRefreshTrigger((n) => n + 1)
  }, [])

  const handleCreate = async () => {
    setFeedback(null)
    if (!selectedShowId) return setFeedback({ type: 'error', message: 'Veuillez sélectionner un spectacle' })
    if (!date) return setFeedback({ type: 'error', message: 'Veuillez sélectionner une date' })

    setLoading(true)
    try {
      const res = await createSchedule({ show_id: parseInt(selectedShowId), date, time: time || '00:00' })
      if (res.success) {
        setFeedback({ type: 'success', message: `Séance créée — ${res.seats_created} sièges générés` })
        setSelectedShowId('')
        setDate(null)
        setTime('')
        await refreshShows()
        window.dispatchEvent(new CustomEvent('schedules_refresh'))
        setTimeout(() => setFeedback(null), 4000)
      } else {
        setFeedback({ type: 'error', message: res.message || 'Erreur lors de la création' })
      }
    } catch (err) {
      setFeedback({ type: 'error', message: err?.body?.message || 'Erreur lors de la création' })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSchedule = async (showId, scheduleId) => {
    if (!window.confirm('Supprimer cette séance ?')) return
    try {
      await deleteSchedule(showId, scheduleId)
      setFeedback({ type: 'success', message: 'Séance supprimée' })
      setTimeout(() => setFeedback(null), 4000)
      await refreshShows()
      window.dispatchEvent(new CustomEvent('schedules_refresh'))
    } catch (err) {
      setFeedback({ type: 'error', message: err?.body?.message || 'Erreur lors de la suppression' })
    }
  }

  const hasSchedules = shows.some((s) => s.schedules && s.schedules.length > 0)

  return (
    <Box sx={{ mt: 3 }}>
      {/* ── Page title ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <CalendarMonthIcon />
        <Typography variant='h6' sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
          Gestion des séances
        </Typography>
      </Box>

      {/* ── Existing schedules ─────────────────────────────────────── */}
      {hasSchedules && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}dd, ${theme.palette.primary.light}dd)`
            }}
          >
            <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
              Séances existantes
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {shows
              .filter((s) => s.schedules && s.schedules.length > 0)
              .map((show) => (
                <ShowGroup key={show.id} show={show} onDeleteSchedule={handleDeleteSchedule} />
              ))}
          </Box>
        </Card>
      )}

      {/* ── Create form ────────────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper
        }}
      >
        {/* Gradient header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}dd, ${theme.palette.primary.light}dd)`
          }}
        >
          <Box sx={{ display: 'flex', color: 'text.secondary', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            <Typography variant='subtitle1' sx={{ fontWeight: 700, letterSpacing: -0.2 }}>
              Nouvelle séance
            </Typography>
          </Box>
          <Typography variant='caption' sx={{ color: 'text.secondary', opacity: 0.85, mt: 0.3, display: 'block' }}>
            Sélectionnez un spectacle, une date et une heure
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Show select */}
            <TextField
              label='Spectacle'
              select
              fullWidth
              required
              value={selectedShowId}
              onChange={(e) => setSelectedShowId(e.target.value)}
              sx={{ flex: '1 1 220px' }}
              InputProps={{ sx: { '& .MuiOutlinedInput-notchedOutline': { borderRadius: 2 } } }}
            >
              {shows.map((show) => (
                <MenuItem key={show.id} value={show.id}>
                  {show.title}
                </MenuItem>
              ))}
            </TextField>

            {/* Date input */}
            <Box sx={{ flex: '0 1 200px', position: 'relative' }}>
              <Box
                onClick={() => {
                  document.getElementById('date-input')?.showPicker()
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 48,
                  px: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  cursor: 'pointer',
                  transition: 'border-color .15s',
                  '&:hover': { borderColor: `${theme.palette.primary.main}70` },
                  '&:focus-within': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 2px ${theme.palette.primary.main}30` }
                }}
              >
                <CalendarMonthIcon sx={{ color: theme.palette.text.secondary, flexShrink: 0 }} />
                <span
                  style={{
                    color: date ? theme.palette.text.primary : theme.palette.text.secondary,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {date ? new Date(date + 'T00:00:00').toLocaleDateString('fr-FR') : 'Date'}
                </span>
                <input
                  id='date-input'
                  type='date'
                  min={dayjs().add(1, 'day').format('YYYY-MM-DD')}
                  value={date || ''}
                  onChange={(e) => setDate(e.target.value || null)}
                  style={{
                    position: 'absolute',
                    width: 0,
                    height: 0,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(100%)'
                  }}
                />
              </Box>
            </Box>

            {/* Time select */}
            <TextField
              label='Heure'
              select
              fullWidth
              value={time}
              onChange={(e) => setTime(e.target.value)}
              sx={{ flex: '0 1 160px' }}
              InputProps={{ sx: { '& .MuiOutlinedInput-notchedOutline': { borderRadius: 2 } } }}
            >
              {TIME_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>

            {/* Create button */}
            <Button
              variant='contained'
              onClick={handleCreate}
              disabled={loading || !selectedShowId || !date}
              sx={{
                flex: '0 1 auto',
                px: 4,
                py: 1.25,
                height: 48,
                mt: 'auto',
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 14,
                textTransform: 'none',
                color: 'text.secondary',
                whiteSpace: 'nowrap'
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'text.secondary' }} /> : 'Créer'}
            </Button>
          </Box>

          {/* Feedback message */}
          {feedback && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                p: 1.25,
                borderRadius: 2,
                backgroundColor: feedback.type === 'success' ? `${theme.palette.success.main}12` : `${theme.palette.error.main}12`,
                border: `1px solid ${feedback.type === 'success' ? theme.palette.success.main + '30' : theme.palette.error.main + '30'}`,
                color: feedback.type === 'success' ? theme.palette.success.main : theme.palette.error.main,
                animation: 'fadeIn .2s ease'
              }}
            >
              {feedback.type === 'success' ? (
                <CheckIcon />
              ) : (
                <>
                  <IconButton size='small' onClick={() => setFeedback(null)} sx={{ color: 'inherit' }}>
                    <CloseIcon />
                  </IconButton>
                </>
              )}
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {feedback.message}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  )
}

AdminScheduleManagement.propTypes = {}
